/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { Button, Classes, Divider, NonIdealState, Icon as BlueprintIcons } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons'; 
import { SavedStatesActionCreator } from 'hooks/savedViews/actions';
import { useSavedStateSelectors } from 'hooks/savedViews/hooks';
import { WIDGETS } from 'widgets';
import { Icon } from 'components/icon';
import { Header } from 'views/savedViews/header';
import { ManageViewsActionCreator, ManageViewsStateContext } from './useManageViewsState';
import { Dialog } from 'components/dialogs';
import { AddDescriptionDialog } from 'views/savedViews/dialogs/addDescriptionDialog';

export function EditDashboard({
	dashboard: oldDashboard,
	onDashboardFinished,
	toggleFavorite
}) {
	const { dispatch } = useSavedStateSelectors();
	const { state: { isCreating, layout }, dispatch: manageViewsDispatch } = useContext(ManageViewsStateContext); 
	const [dashboard, setDashboard] = useState(oldDashboard);
	const [isAddTitleModalOpen, setAddTitleModalOpen] = useState(false);
	const [isLeaveDashboardModalOpen, setLeaveDashboardModalOpen] = useState(false); 
	const [isRemoveWidgetModalOpen, setRemoveWidgetModalOpen] = useState(false);
	const [isEmptyWidgetModalOpen, setEmptyWidgetModalOpen] = useState(false); 
	const [isAddDescriptionModalOpen, setIsAddDescriptionModalOpen] = useState(false);
	const [widgetKey, setWidgetKey] = useState(0); 

	const openSaveTitleWarning = () => { 
		setAddTitleModalOpen(true);
	}

	const closeSaveTitleWarning = () => { 
		setAddTitleModalOpen(false); 
	}

	const openLeaveDashboardWarning = () => {
		setLeaveDashboardModalOpen(true); 
	}

	const closeLeaveDashboardWarning = () => { 
		setLeaveDashboardModalOpen(false); 
	}

	const openRemoveWidgetWarning = (i) => {
		setWidgetKey(i);  
		setRemoveWidgetModalOpen(true); 
	}

	const closeRemoveWidgetWarning = () => { 
		setRemoveWidgetModalOpen(false); 
	}

	const openEmptyWidgetWarning = () => { 
		setEmptyWidgetModalOpen(true); 
	}

	const closeEmptyWidgetWarning = () => { 
		setEmptyWidgetModalOpen(false); 
	}

	const toggleDescriptionDialog = () => {
		setIsAddDescriptionModalOpen(state => !state);
	}

	const handleAddDescription = (description) => {
		setDashboard(dash => ({
			...dash,
			metaData: {
				...dash.metaData,
				description: description
			}
		}));
		toggleDescriptionDialog();
	}

	// Force Number of cards to be === to layout.cardCount
	useEffect(() => {
		setDashboard((oldDash) => {
			let cards = [
				// Keep filled cards
				...oldDash.cards?.filter((x) => !!x),
				// NOTE: if we have a layout with more than 10 we'll have to generalize this code
				...Array.from(new Array(10)).map(() => null),
			];
			cards = cards.slice(0, layout.cardCount);
			return {
				...oldDash,
				cards,
			};
		});
	}, [layout.cardCount]);


	const onClose = useCallback(() => {
		// re-routes to manage views "home" page
		manageViewsDispatch(ManageViewsActionCreator.setSelectedView(null));
		onDashboardFinished();
	}, [onDashboardFinished]);

	const checkWidgets = () => { 
		dashboard.cards.some(card => card === null) ? openEmptyWidgetWarning() : onSave();
	}

	// Handle save
	const onSave = useCallback(() => {
		if (dashboard.metaData.name === ''){ 
			openSaveTitleWarning();
			return;
		}
		const updatedView = {
			...dashboard,
			cards: dashboard.cards?.filter((x) => !!x),
			layout: {
				layoutStyle: {
					key: layout.key,
					cardCount: layout.cardCount
				}
			},
		}
		dispatch(SavedStatesActionCreator.updateView(updatedView));
		manageViewsDispatch(ManageViewsActionCreator.setSelectedView(updatedView));
		onDashboardFinished();
	}, [dashboard, dispatch, manageViewsDispatch, onDashboardFinished, layout]);

	const onNameChange = (name) => {
		setDashboard(prevDashboard => ({
			...prevDashboard,
			metaData: {
				...prevDashboard.metaData,
				name: name
			}
		}));
	}

	// Puts a widget in a cell
	const setWidget = useCallback((ev, i) => {
		ev.preventDefault();
		const view = ev.dataTransfer.getData('view');
		if (!view) return;
		setDashboard((oldDash) => {
			let newCards = [...oldDash.cards];
			try {
				newCards[i] = JSON.parse(view);
			}
			catch (err) {
				console.error(err);
			}
			return {
				...oldDash,
				cards: newCards,
			};
		});
	}, []);

	// Removes a widget from a cell
	const clearWidget = useCallback((i) => {
		closeRemoveWidgetWarning(); 
		setDashboard((oldDash) => {
			let newCards = [...oldDash.cards];
			newCards[i] = null;
			return {
				...oldDash,
				cards: newCards,
			};
		});
	}, []);

	const handleFavoriteToggle = useCallback(() => {
		setDashboard(prevDashboard => ({
			...prevDashboard,
			metaData: {
				...prevDashboard.metaData,
				isFavorite: !prevDashboard.metaData.isFavorite
			}
		}));
		if (!isCreating) {
			toggleFavorite();
		}
	}, [isCreating, setDashboard, toggleFavorite]);
	
	return (
		<Fragment>
			<Header
				viewName={dashboard.metaData.name}
				onRename={onNameChange}
				onCancel={openLeaveDashboardWarning}
				onSave={checkWidgets}
				toggleFavorite={handleFavoriteToggle}
				isFavorite={isCreating ? dashboard.metaData.isFavorite : oldDashboard.metaData.isFavorite}
				onAddDescription={toggleDescriptionDialog}
			/>
			{isAddTitleModalOpen && 
				<Dialog style={styles.Dialog} 
					title = {"Add Dashboard Title"}
					body = { 
						<div className={Classes.DIALOG_BODY}>
							<p>Before we can save your dashboard, add a title.</p> 
						</div> 
					}
					icon={<BlueprintIcons icon={IconNames.MANUALLY_ENTERED_DATA} intent={"none"}/>}
					onClose={closeSaveTitleWarning} 
					footer = {
						<div className={Classes.DIALOG_FOOTER} >
							<Button outlined={true} css={css `padding-left: 15px; padding-right: 15px;`} intent="primary" onClick={closeSaveTitleWarning}>OK</Button>
						</div>
					}
				/>
			}
			{isLeaveDashboardModalOpen && 
				<Dialog style={styles.Dialog} 
					title={"Unsaved Changes"}
					body={
						<div className={Classes.DIALOG_BODY}>
							<p>You are leaving the dashboard builder. Any unsaved changes will be lost.</p> 
						</div> 
					}
					icon={<BlueprintIcons icon={IconNames.WARNING_SIGN} intent={"warning"}/>}
					onClose={closeLeaveDashboardWarning} 
					footer={
						<div className={Classes.DIALOG_FOOTER}>
							<Button minimal={true} css={css `.bp3-button-text {color: #5c7080;}`} onClick={closeLeaveDashboardWarning}>Stay in Dashboard Builder</Button> 
							<Button outlined={true} intent="primary" onClick={onClose}>Leave</Button>
						</div>
					}
				/>
			}
			{isEmptyWidgetModalOpen && <Dialog style={styles.Dialog} 
					title="Empty Widget Container"
					body = {
						<div className={Classes.DIALOG_BODY}>
							<p>There is an empty widget container. Do you wish to save it as is?</p> 
						</div> 
					}
					icon={<BlueprintIcons icon={IconNames.WIDGET} intent={"none"}/>}
					onClose={closeEmptyWidgetWarning} 
					footer = {
						<div className={Classes.DIALOG_FOOTER} >
							<Button minimal={true} css={css `.bp3-button-text {color: #5c7080;}`} onClick={closeEmptyWidgetWarning}>Cancel</Button> 
							<Button outlined={true} onClick={onSave} intent="primary">Save</Button>
						</div>
					}
				/>
			}
			{isRemoveWidgetModalOpen && 
				<Dialog style={styles.Dialog} 
					title={"Remove Widget"} 
					body = {
						<div className={Classes.DIALOG_BODY}>
							<p>Are you sure you want to remove this widget?</p> 
						</div> 
					}
					icon={<BlueprintIcons icon={IconNames.TRASH} intent={"danger"}/>}
					onClose={closeRemoveWidgetWarning} 
					footer = {
						<div className={Classes.DIALOG_FOOTER} >
							<Button minimal={true} css={css `.bp3-button-text {color: #5c7080;}`} onClick={closeRemoveWidgetWarning}>Cancel</Button> 
							<Button outlined={true} onClick={() => clearWidget(widgetKey)} intent="primary">Remove</Button>
						</div>
					}
				/>
			}
			{isAddDescriptionModalOpen &&
				<AddDescriptionDialog closeDialog={toggleDescriptionDialog} handleSubmit={handleAddDescription} />
			}
			<Divider css={css`margin-left: -20px; margin-right: -20px; padding-top: 10px;`}/>
			<div
				css={[
					css`
						flex: 1 1 auto;
						display: grid;
						row-gap: 5px;
						column-gap: 5px;
					`,
					layout.styles,
				]}
			>
				{dashboard.cards?.map((card, i) => {
					return (
						<div
							key={i}
							css={css`
								background: #ededed;
								border-radius: 5px;
								display: flex;
								justify-content: center;
								align-items: center;
							`}
							onDrop={(e) => setWidget(e, i)}
							onDragOver={(e) => e.preventDefault()}
						>
							{!card ? (
								<div>Drop Widget Here</div>
							) : (
								<NonIdealState
									icon={<Icon icon={WIDGETS[card.type].icon} iconSize={60} />}
									title={card.metaData.name}
									action={
										<Button
											className={Classes.MINIMAL}
											icon={'trash'}
											onClick={() => openRemoveWidgetWarning(i)}
										>
											Remove
										</Button>
									}
								/>
							)}
						</div>
					);
				})}
			</div>
		</Fragment>
	);
}

const styles = { 
	Dialog: css`
	.bp3-button{ 
		margin-left: 5px; 
	}

	.bp3-dialog-footer{ 
		text-align: right; 
	}

	background: white; 
`,	
}
